/**
 * Clip Editor Module
 * 
 * Provides UI for editing clips with integrated validation,
 * regeneration controls, and workflow actions.
 * 
 * Implements Issue #234 validation integration requirements.
 */

import logger from '../../core/logger.js';
import { adminClipsService } from '../../services/admin-clips-service.js';
import { can } from '../../utils/rbac-helpers.js';
import { Permission, ClipState } from '../../types/rbac.js';
import { AdminClip, UpdateClipRequest, Job } from '../../types/admin-clip.js';
import { 
  renderValidationReport, 
  createValidationStatusIndicator 
} from '../../ui/admin/validation-report-viewer.js';
import { 
  showRegenerationDialog, 
  showJobStatusDialog 
} from '../../ui/admin/regeneration-dialog.js';
import { showAudioGenerationDialog } from '../../ui/admin/audio-generation-dialog.js';
import toast from '../../ui/toast.js';

/**
 * Clip editor callbacks
 */
interface ClipEditorCallbacks {
  onClose: () => void;
  onSave: (clipId: string) => void;
}

// NOTE: Module-level state is used for simplicity in this implementation.
// For multi-tab/multi-window scenarios, consider refactoring to class-based
// state management or passing state through function parameters.
let callbacks: ClipEditorCallbacks | null = null;
let currentClip: AdminClip | null = null;
let hasUnsavedChanges = false;

/**
 * Register callbacks
 */
export function registerClipEditorCallbacks(cbs: ClipEditorCallbacks): void {
  callbacks = cbs;
}

/**
 * Render the clip editor
 */
export async function renderClipEditor(
  container: HTMLElement,
  clipId: string
): Promise<void> {
  logger.info('admin: Rendering clip editor', { clipId });
  
  // Clear container
  container.innerHTML = '';
  
  // Check permissions
  if (!can(Permission.VIEW_JOBS)) {
    renderAccessDenied(container);
    return;
  }
  
  try {
    // Fetch clip
    const clip = await adminClipsService.getClip(clipId);
    currentClip = clip;
    hasUnsavedChanges = false;
    
    // Debug logging to diagnose format issue
    logger.info('admin: Clip loaded for editor', { 
      clipId, 
      format: clip.format,
      hasFormat: 'format' in clip,
      clipKeys: Object.keys(clip)
    });
    
    // Create editor layout
    const editor = document.createElement('div');
    editor.className = 'clip-editor';
    
    // Header
    const header = createEditorHeader(clip);
    editor.appendChild(header);
    
    // Validation section (always visible)
    const validationSection = createValidationSection(clip);
    editor.appendChild(validationSection);
    
    // Editor tabs
    const tabs = createEditorTabs(clip);
    editor.appendChild(tabs);
    
    // Content area
    const contentArea = document.createElement('div');
    contentArea.id = 'editor-content-area';
    contentArea.className = 'editor-content-area';
    editor.appendChild(contentArea);
    
    // Actions footer
    const actions = createEditorActions(clip);
    editor.appendChild(actions);
    
    container.appendChild(editor);
    
    // Render default tab (metadata)
    renderMetadataTab(contentArea, clip);
    
  } catch (error) {
    logger.error('admin: Failed to load clip', { clipId, error });
    container.innerHTML = '<div style="padding: 2rem; color: #dc2626;">Failed to load clip</div>';
  }
}

/**
 * Create editor header
 */
function createEditorHeader(clip: AdminClip): HTMLElement {
  const header = document.createElement('div');
  header.className = 'editor-header';
  
  const titleRow = document.createElement('div');
  titleRow.className = 'editor-header__title-row';
  
  const backBtn = document.createElement('button');
  backBtn.className = 'btn btn--secondary';
  backBtn.textContent = '← Back';
  backBtn.addEventListener('click', () => {
    if (hasUnsavedChanges) {
      if (!confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return;
      }
    }
    if (callbacks) callbacks.onClose();
  });
  titleRow.appendChild(backBtn);
  
  const title = document.createElement('h1');
  title.className = 'editor-header__title';
  title.textContent = clip.title || 'Untitled Clip';
  titleRow.appendChild(title);
  
  header.appendChild(titleRow);
  
  // Metadata row
  const metaRow = document.createElement('div');
  metaRow.className = 'editor-header__meta';
  
  const languageBadge = document.createElement('span');
  languageBadge.className = 'clip-meta-badge';
  languageBadge.textContent = clip.language.toUpperCase();
  metaRow.appendChild(languageBadge);
  
  const levelBadge = document.createElement('span');
  levelBadge.className = 'clip-meta-badge';
  levelBadge.textContent = String(clip.level);
  metaRow.appendChild(levelBadge);
  
  const formatBadge = document.createElement('span');
  formatBadge.className = 'clip-meta-badge';
  formatBadge.textContent = String(clip.format);
  metaRow.appendChild(formatBadge);
  
  const stateBadge = document.createElement('span');
  stateBadge.className = 'clip-meta-badge clip-meta-badge--state';
  stateBadge.textContent = String(clip.state);
  metaRow.appendChild(stateBadge);
  
  header.appendChild(metaRow);
  
  return header;
}

/**
 * Create validation section
 */
function createValidationSection(clip: AdminClip): HTMLElement {
  const section = document.createElement('div');
  section.className = 'editor-validation-section';
  section.id = 'validation-section';
  
  const sectionHeader = document.createElement('div');
  sectionHeader.className = 'editor-section-header';
  
  const sectionTitle = document.createElement('h2');
  sectionTitle.textContent = 'Validation';
  sectionHeader.appendChild(sectionTitle);
  
  // Validation actions
  if (can(Permission.SUBMIT_FOR_REVIEW) || can(Permission.APPROVE_TEXT)) {
    const actionsRow = document.createElement('div');
    actionsRow.className = 'validation-actions';
    
    const validateBtn = document.createElement('button');
    validateBtn.className = 'btn btn--primary';
    validateBtn.textContent = '▶ Run Validation';
    validateBtn.addEventListener('click', () => handleValidateClip(clip.id));
    actionsRow.appendChild(validateBtn);
    
    // Add regenerate button for editors and admins
    if (can(Permission.SUBMIT_FOR_REVIEW)) {
      const regenerateBtn = document.createElement('button');
      regenerateBtn.className = 'btn btn--secondary';
      regenerateBtn.textContent = '🔄 Regenerate Script';
      regenerateBtn.addEventListener('click', () => handleRegenerateScript(clip));
      actionsRow.appendChild(regenerateBtn);
    }
    
    sectionHeader.appendChild(actionsRow);
  }
  
  section.appendChild(sectionHeader);
  
  // Validation status indicator
  const needsValidation = hasUnsavedChanges;
  const statusIndicator = createValidationStatusIndicator(
    clip.validationReport || null,
    needsValidation
  );
  section.appendChild(statusIndicator);
  
  // Validation report (if available)
  if (clip.validationReport) {
    const reportContainer = document.createElement('div');
    reportContainer.id = 'validation-report-container';
    renderValidationReport(clip.validationReport, reportContainer, { 
      collapsible: true,
      defaultCollapsed: clip.validationReport.overallStatus === 'passed'
    });
    section.appendChild(reportContainer);
  } else {
    const noReport = document.createElement('div');
    noReport.className = 'no-validation-report';
    noReport.textContent = 'No validation report available. Click "Run Validation" to validate this clip.';
    section.appendChild(noReport);
  }
  
  return section;
}

/**
 * Create structured preview groups for regeneration output
 */
function createRegenerationPreviewGroups(clip: AdminClip, outputs: Record<string, any>): Array<{
  title: string;
  fields: Array<{ label: string; preview: string; changed: boolean }>;
}> {
  const groups = [];
  
  // Metadata group
  const metadataFields = [];
  
  if (outputs['title']) {
    metadataFields.push({
      label: 'Title',
      preview: outputs['title'],
      changed: outputs['title'] !== clip.title
    });
  }
  
  if (outputs['wordCount']) {
    metadataFields.push({
      label: 'Word Count',
      preview: String(outputs['wordCount']),
      changed: outputs['wordCount'] !== clip.wordCount
    });
  }
  
  if (outputs['tags'] && Array.isArray(outputs['tags'])) {
    const currentTags = ((clip.metadata as any)?.['tags'] as string[]) || [];
    metadataFields.push({
      label: 'Tags',
      preview: outputs['tags'].join(', '),
      changed: JSON.stringify(outputs['tags'].sort()) !== JSON.stringify(currentTags.sort())
    });
  }
  
  if (outputs['metadata']?.['theme']) {
    const currentTheme = ((clip.metadata as any)?.['theme'] as string) || '';
    metadataFields.push({
      label: 'Theme',
      preview: outputs['metadata']['theme'],
      changed: outputs['metadata']['theme'] !== currentTheme
    });
  }
  
  if (metadataFields.length > 0) {
    groups.push({
      title: 'Metadata',
      fields: metadataFields
    });
  }
  
  // Speakers group (for dialogue clips)
  if (outputs['speakers'] && Array.isArray(outputs['speakers'])) {
    const speakersFields = outputs['speakers'].map((speaker: any, idx: number) => {
      const currentSpeaker = clip.speakers?.[idx];
      const speakerName = typeof speaker === 'string' ? speaker : speaker.name;
      // Use alternating genders as balanced defaults if missing (consistent with backend)
      const speakerGender = typeof speaker === 'object' && speaker.gender ? speaker.gender : (idx % 2 === 0 ? 'female' : 'male');
      const currentName = currentSpeaker ? (typeof currentSpeaker === 'string' ? currentSpeaker : currentSpeaker.name) : '';
      const currentGender = currentSpeaker && typeof currentSpeaker === 'object' ? currentSpeaker.gender : '';
      
      return {
        label: `Speaker ${idx + 1}`,
        preview: `${speakerName} (${speakerGender})`,
        changed: speakerName !== currentName || speakerGender !== currentGender
      };
    });
    
    groups.push({
      title: 'Speakers',
      fields: speakersFields
    });
  }
  
  // Script group
  const scriptFields = [];
  if (outputs['script']) {
    let scriptPreview = '';
    if (Array.isArray(outputs['script'])) {
      // Dialogue - show first 2 turns
      scriptPreview = outputs['script'].slice(0, 2)
        .map((turn: any) => `${turn.speaker}: ${turn.text.slice(0, 60)}...`)
        .join(' | ');
      if (outputs['script'].length > 2) {
        scriptPreview += ` (${outputs['script'].length} turns total)`;
      }
    } else if (typeof outputs['script'] === 'string') {
      // Monologue - show first 100 chars
      scriptPreview = outputs['script'].slice(0, 100) + (outputs['script'].length > 100 ? '...' : '');
    }
    
    scriptFields.push({
      label: 'Script',
      preview: scriptPreview,
      changed: true // Always mark as changed since we're regenerating
    });
  }
  
  if (outputs['questions'] && Array.isArray(outputs['questions'])) {
    scriptFields.push({
      label: 'Questions',
      preview: `${outputs['questions'].length} questions (${outputs['questions'].map((q: any) => q.type).join(', ')})`,
      changed: true // Always mark as changed
    });
  }
  
  if (scriptFields.length > 0) {
    groups.push({
      title: 'Script & Questions',
      fields: scriptFields
    });
  }
  
  // Story fields group (if present)
  const storyFields = [];
  if (outputs['metadata']?.['storyId'] || outputs['metadata']?.['storyName'] || outputs['metadata']?.['storyPart']) {
    if (outputs['metadata']['storyId']) {
      storyFields.push({
        label: 'Story ID',
        preview: outputs['metadata']['storyId'],
        changed: outputs['metadata']['storyId'] !== ((clip.metadata as any)?.['storyId'] as string)
      });
    }
    if (outputs['metadata']['storyName']) {
      storyFields.push({
        label: 'Story Name',
        preview: outputs['metadata']['storyName'],
        changed: outputs['metadata']['storyName'] !== ((clip.metadata as any)?.['storyName'] as string)
      });
    }
    if (outputs['metadata']['storyPart']) {
      storyFields.push({
        label: 'Story Part',
        preview: String(outputs['metadata']['storyPart']),
        changed: outputs['metadata']['storyPart'] !== ((clip.metadata as any)?.['storyPart'] as number)
      });
    }
  }
  
  if (storyFields.length > 0) {
    groups.push({
      title: 'Story Arc',
      fields: storyFields
    });
  }
  
  return groups;
}

/**
 * Create editor tabs
 */
function createEditorTabs(clip: AdminClip): HTMLElement {
  const tabsContainer = document.createElement('div');
  tabsContainer.className = 'editor-tabs';
  
  const tabs = [
    { id: 'metadata', label: 'Metadata' },
    { id: 'script', label: 'Script' },
    { id: 'questions', label: 'Questions' },
    { id: 'audio', label: 'Audio' },
    { id: 'history', label: 'History' }
  ];
  
  tabs.forEach(tab => {
    const tabBtn = document.createElement('button');
    tabBtn.className = 'editor-tab';
    tabBtn.textContent = tab.label;
    tabBtn.dataset['tabId'] = tab.id;
    
    if (tab.id === 'metadata') {
      tabBtn.classList.add('editor-tab--active');
    }
    
    tabBtn.addEventListener('click', () => {
      // Update active tab
      document.querySelectorAll('.editor-tab').forEach(t => {
        t.classList.remove('editor-tab--active');
      });
      tabBtn.classList.add('editor-tab--active');
      
      // Render tab content
      const contentArea = document.getElementById('editor-content-area');
      if (contentArea && tabBtn.dataset['tabId']) {
        renderTabContent(contentArea, clip, tabBtn.dataset['tabId']);
      }
    });
    
    tabsContainer.appendChild(tabBtn);
  });
  
  return tabsContainer;
}

/**
 * Render tab content
 */
function renderTabContent(
  container: HTMLElement,
  clip: AdminClip,
  tabId: string
): void {
  switch (tabId) {
    case 'metadata':
      renderMetadataTab(container, clip);
      break;
    case 'script':
      renderScriptTab(container, clip).catch(err => {
        logger.error('admin: Failed to render script tab', { error: err });
        toast.error('Failed to load script tab');
      });
      break;
    case 'questions':
      renderQuestionsTab(container, clip);
      break;
    case 'audio':
      renderAudioTab(container, clip);
      break;
    case 'history':
      renderHistoryTab(container, clip);
      break;
  }
}

/**
 * Render metadata tab
 */
function renderMetadataTab(container: HTMLElement, clip: AdminClip): void {
  const tabContent = document.createElement('div');
  tabContent.className = 'editor-tab-content';
  
  // Title field
  const titleGroup = document.createElement('div');
  titleGroup.className = 'form-group';
  
  const titleLabel = document.createElement('label');
  titleLabel.setAttribute('for', 'clip-title');
  titleLabel.textContent = 'Title';
  titleGroup.appendChild(titleLabel);
  
  const titleInput = document.createElement('input');
  titleInput.type = 'text';
  titleInput.id = 'clip-title';
  titleInput.className = 'form-input';
  titleInput.value = clip.title || '';
  titleInput.addEventListener('input', () => markAsChanged());
  titleGroup.appendChild(titleInput);
  
  tabContent.appendChild(titleGroup);
  
  // Form row for language, level, format
  const formRow = document.createElement('div');
  formRow.className = 'form-row';
  
  // Language select
  const languageGroup = document.createElement('div');
  languageGroup.className = 'form-group';
  const languageLabel = document.createElement('label');
  languageLabel.setAttribute('for', 'clip-language');
  languageLabel.textContent = 'Language';
  languageGroup.appendChild(languageLabel);
  
  const languageSelect = document.createElement('select');
  languageSelect.id = 'clip-language';
  languageSelect.className = 'form-input';
  ['tr', 'de', 'en'].forEach(lang => {
    const option = document.createElement('option');
    option.value = lang;
    option.textContent = { tr: 'Turkish', de: 'German', en: 'English' }[lang] || lang;
    option.selected = clip.language === lang;
    languageSelect.appendChild(option);
  });
  languageSelect.addEventListener('change', () => markAsChanged());
  languageGroup.appendChild(languageSelect);
  formRow.appendChild(languageGroup);
  
  // Level select
  const levelGroup = document.createElement('div');
  levelGroup.className = 'form-group';
  const levelLabel = document.createElement('label');
  levelLabel.setAttribute('for', 'clip-level');
  levelLabel.textContent = 'Level';
  levelGroup.appendChild(levelLabel);
  
  const levelSelect = document.createElement('select');
  levelSelect.id = 'clip-level';
  levelSelect.className = 'form-input';
  ['B2', 'B1'].forEach(level => {
    const option = document.createElement('option');
    option.value = level;
    option.textContent = level;
    option.selected = clip.level === level;
    levelSelect.appendChild(option);
  });
  levelSelect.addEventListener('change', () => markAsChanged());
  levelGroup.appendChild(levelSelect);
  formRow.appendChild(levelGroup);
  
  // Format select
  const formatGroup = document.createElement('div');
  formatGroup.className = 'form-group';
  const formatLabel = document.createElement('label');
  formatLabel.setAttribute('for', 'clip-format');
  formatLabel.textContent = 'Format';
  formatGroup.appendChild(formatLabel);
  
  const formatSelect = document.createElement('select');
  formatSelect.id = 'clip-format';
  formatSelect.className = 'form-input';
  ['dialogue', 'monologue', 'mixed'].forEach(format => {
    const option = document.createElement('option');
    option.value = format;
    option.textContent = format.charAt(0).toUpperCase() + format.slice(1);
    option.selected = clip.format === format;
    formatSelect.appendChild(option);
  });
  formatSelect.addEventListener('change', () => markAsChanged());
  formatGroup.appendChild(formatSelect);
  formRow.appendChild(formatGroup);
  
  tabContent.appendChild(formRow);
  
  // Clip ID (readonly)
  const idGroup = document.createElement('div');
  idGroup.className = 'form-group';
  const idLabel = document.createElement('label');
  idLabel.textContent = 'Clip ID';
  idGroup.appendChild(idLabel);
  const idInput = document.createElement('input');
  idInput.type = 'text';
  idInput.className = 'form-input';
  idInput.value = clip.id;
  idInput.readOnly = true;
  idGroup.appendChild(idInput);
  tabContent.appendChild(idGroup);
  
  // Created date (readonly)
  const createdGroup = document.createElement('div');
  createdGroup.className = 'form-group';
  const createdLabel = document.createElement('label');
  createdLabel.textContent = 'Created';
  createdGroup.appendChild(createdLabel);
  const createdInput = document.createElement('input');
  createdInput.type = 'text';
  createdInput.className = 'form-input';
  createdInput.value = new Date(clip.createdAt).toLocaleString();
  createdInput.readOnly = true;
  createdGroup.appendChild(createdInput);
  tabContent.appendChild(createdGroup);
  
  // Updated date (readonly)
  const updatedGroup = document.createElement('div');
  updatedGroup.className = 'form-group';
  const updatedLabel = document.createElement('label');
  updatedLabel.textContent = 'Last Updated';
  updatedGroup.appendChild(updatedLabel);
  const updatedInput = document.createElement('input');
  updatedInput.type = 'text';
  updatedInput.className = 'form-input';
  updatedInput.value = new Date(clip.updatedAt).toLocaleString();
  updatedInput.readOnly = true;
  updatedGroup.appendChild(updatedInput);
  tabContent.appendChild(updatedGroup);
  
  container.innerHTML = '';
  container.appendChild(tabContent);
}

/**
 * Render regeneration results section
 */
async function renderRegenerationResultsSection(container: HTMLElement, clip: AdminClip): Promise<void> {
  const section = document.createElement('div');
  section.className = 'regeneration-results-section';
  section.style.marginBottom = '1.5rem';
  section.style.padding = '1rem';
  section.style.border = '1px solid #ddd';
  section.style.borderRadius = '4px';
  section.style.backgroundColor = '#f9f9f9';
  
  const title = document.createElement('h3');
  title.textContent = 'Regeneration Results';
  title.style.marginTop = '0';
  title.style.marginBottom = '1rem';
  title.style.fontSize = '1.1rem';
  title.style.fontWeight = '600';
  section.appendChild(title);
  
  // Fetch latest regeneration job
  const latestJob = await adminClipsService.getLatestRegenerationJob(clip.id);
  
  if (!latestJob) {
    const helpText = document.createElement('p');
    helpText.textContent = 'No regeneration jobs found for this clip. Use the "Regenerate" button in the top toolbar to create one.';
    helpText.style.color = '#666';
    helpText.style.fontStyle = 'italic';
    section.appendChild(helpText);
  } else {
    // Show job info
    const jobInfo = document.createElement('div');
    jobInfo.style.marginBottom = '1rem';
    
    const statusBadge = document.createElement('span');
    statusBadge.textContent = latestJob.status;
    statusBadge.className = `status-badge status-badge--${latestJob.status}`;
    statusBadge.style.padding = '0.25rem 0.5rem';
    statusBadge.style.borderRadius = '4px';
    statusBadge.style.fontSize = '0.875rem';
    statusBadge.style.fontWeight = '600';
    statusBadge.style.textTransform = 'uppercase';
    
    if (latestJob.status === 'queued') {
      statusBadge.style.backgroundColor = '#fef3c7';
      statusBadge.style.color = '#92400e';
    } else if (latestJob.status === 'running') {
      statusBadge.style.backgroundColor = '#dbeafe';
      statusBadge.style.color = '#1e40af';
    } else if (latestJob.status === 'succeeded') {
      statusBadge.style.backgroundColor = '#d1fae5';
      statusBadge.style.color = '#065f46';
    } else if (latestJob.status === 'failed') {
      statusBadge.style.backgroundColor = '#fee2e2';
      statusBadge.style.color = '#991b1b';
    }
    
    const jobText = document.createElement('span');
    jobText.textContent = ` Job created ${new Date(latestJob.created_at).toLocaleString()}`;
    jobText.style.marginLeft = '0.5rem';
    
    jobInfo.appendChild(statusBadge);
    jobInfo.appendChild(jobText);
    section.appendChild(jobInfo);
    
    // Add actions based on status
    const actions = document.createElement('div');
    actions.style.display = 'flex';
    actions.style.gap = '0.75rem';
    
    if (latestJob.status === 'queued' || latestJob.status === 'running') {
      const openStatusBtn = document.createElement('button');
      openStatusBtn.className = 'btn btn--secondary';
      openStatusBtn.textContent = 'Open Job Status';
      openStatusBtn.addEventListener('click', () => {
        showJobStatusDialog(latestJob.id);
        // Note: Job status dialog doesn't support callbacks yet
        // User can refresh the page to see updated status
      });
      actions.appendChild(openStatusBtn);
    } else if (latestJob.status === 'succeeded' && latestJob.outputs) {
      // Show structured preview of regenerated content with field-by-field diff
      const preview = document.createElement('div');
      preview.style.marginBottom = '1rem';
      preview.style.padding = '0.75rem';
      preview.style.backgroundColor = '#ffffff';
      preview.style.border = '1px solid #e5e7eb';
      preview.style.borderRadius = '4px';
      
      const previewTitle = document.createElement('div');
      previewTitle.textContent = 'Regeneration Preview:';
      previewTitle.style.fontWeight = '600';
      previewTitle.style.marginBottom = '0.75rem';
      preview.appendChild(previewTitle);
      
      // Create field groups
      const fieldGroups = createRegenerationPreviewGroups(clip, latestJob.outputs);
      
      fieldGroups.forEach(group => {
        const groupContainer = document.createElement('div');
        groupContainer.style.marginBottom = '1rem';
        
        const groupTitle = document.createElement('div');
        groupTitle.textContent = group.title;
        groupTitle.style.fontWeight = '600';
        groupTitle.style.fontSize = '0.875rem';
        groupTitle.style.color = '#374151';
        groupTitle.style.marginBottom = '0.5rem';
        groupContainer.appendChild(groupTitle);
        
        group.fields.forEach(field => {
          const fieldRow = document.createElement('div');
          fieldRow.style.display = 'flex';
          fieldRow.style.justifyContent = 'space-between';
          fieldRow.style.alignItems = 'flex-start';
          fieldRow.style.padding = '0.5rem';
          fieldRow.style.backgroundColor = field.changed ? '#fef3c7' : '#f9fafb';
          fieldRow.style.borderRadius = '4px';
          fieldRow.style.marginBottom = '0.25rem';
          
          const fieldLabel = document.createElement('div');
          fieldLabel.style.fontWeight = '500';
          fieldLabel.style.fontSize = '0.8125rem';
          fieldLabel.style.color = '#6b7280';
          fieldLabel.style.minWidth = '100px';
          fieldLabel.textContent = field.label + ':';
          
          const fieldValue = document.createElement('div');
          fieldValue.style.fontSize = '0.8125rem';
          fieldValue.style.color = '#1f2937';
          fieldValue.style.flex = '1';
          fieldValue.style.marginLeft = '0.75rem';
          fieldValue.style.maxHeight = '60px';
          fieldValue.style.overflowY = 'auto';
          
          if (field.changed) {
            fieldValue.style.fontWeight = '600';
            const changeIndicator = document.createElement('span');
            changeIndicator.textContent = ' (Changed)';
            changeIndicator.style.color = '#d97706';
            changeIndicator.style.fontWeight = '400';
            fieldValue.textContent = field.preview;
            fieldValue.appendChild(changeIndicator);
          } else {
            fieldValue.textContent = field.preview;
          }
          
          fieldRow.appendChild(fieldLabel);
          fieldRow.appendChild(fieldValue);
          groupContainer.appendChild(fieldRow);
        });
        
        preview.appendChild(groupContainer);
      });
      
      section.appendChild(preview);
      
      // Show validation errors if any
      if (latestJob.outputs['_rawOutput']) {
        // Check if there are validation issues (though job succeeded, we can still show warnings)
        const wordCount = (latestJob.outputs['wordCount'] as number) || 0;
        if (clip.level === 'B2' && clip.language === 'tr' && (wordCount < 210 || wordCount > 240)) {
          const warning = document.createElement('div');
          warning.style.padding = '0.75rem';
          warning.style.backgroundColor = '#fef3c7';
          warning.style.border = '1px solid #fbbf24';
          warning.style.borderRadius = '4px';
          warning.style.marginBottom = '1rem';
          warning.style.fontSize = '0.875rem';
          warning.style.color = '#92400e';
          warning.textContent = `⚠️ Word count (${wordCount}) is outside recommended range (210-240 words for B2 Turkish)`;
          section.appendChild(warning);
        }
      }
      
      const applyBtn = document.createElement('button');
      applyBtn.className = 'btn btn--primary';
      applyBtn.textContent = 'Apply Regeneration';
      applyBtn.addEventListener('click', async () => {
        await handleApplyRegeneration(clip, latestJob);
      });
      actions.appendChild(applyBtn);
      
      const viewJobBtn = document.createElement('button');
      viewJobBtn.className = 'btn btn--secondary';
      viewJobBtn.textContent = 'View Full Job Details';
      viewJobBtn.addEventListener('click', () => {
        showJobStatusDialog(latestJob.id);
      });
      actions.appendChild(viewJobBtn);
    } else if (latestJob.status === 'failed') {
      const errorText = document.createElement('p');
      errorText.textContent = `Error: ${latestJob.error || 'Unknown error'}`;
      errorText.style.color = '#dc2626';
      errorText.style.marginBottom = '0.75rem';
      section.appendChild(errorText);
      
      const viewJobBtn = document.createElement('button');
      viewJobBtn.className = 'btn btn--secondary';
      viewJobBtn.textContent = 'View Job Details';
      viewJobBtn.addEventListener('click', () => {
        showJobStatusDialog(latestJob.id);
      });
      actions.appendChild(viewJobBtn);
    }
    
    section.appendChild(actions);
  }
  
  container.appendChild(section);
}

/**
 * Handle applying regeneration outputs to clip
 */
async function handleApplyRegeneration(clip: AdminClip, job: Job): Promise<void> {
  logger.info('admin: Applying regeneration outputs', { clipId: clip.id, jobId: job.id });
  
  try {
    // Normalize outputs
    const outputs = job.outputs;
    if (!outputs || !outputs['script']) {
      toast.error('Invalid job outputs: missing script');
      return;
    }
    
    // Validate and normalize question types
    const validQuestionTypes = ['gist', 'sequence', 'detail', 'inference', 'cloze'];
    const normalizedQuestions = (outputs['questions'] as any)?.map((q: any) => {
      if (!validQuestionTypes.includes(q.type)) {
        throw new Error(`Unknown question type: ${q.type}`);
      }
      return q;
    }) || [];
    
    // Build update request with full schema support
    const updateRequest: UpdateClipRequest = {
      script: outputs['script'] as string | Array<{ speaker: string; text: string }>,
      questions: normalizedQuestions
    };
    
    // Apply title if provided
    if (outputs['title'] && typeof outputs['title'] === 'string') {
      updateRequest.title = outputs['title'];
    }
    
    // Use speakers from outputs if available, otherwise extract from script
    if (outputs['speakers'] && Array.isArray(outputs['speakers']) && outputs['speakers'].length > 0) {
      updateRequest.speakers = outputs['speakers'];
    } else if (Array.isArray(outputs['script'])) {
      // For dialogue clips, extract speakers from script and normalize
      const speakerNames = new Set<string>();
      (outputs['script'] as Array<any>).forEach((turn: any) => {
        if (turn.speaker) {
          speakerNames.add(turn.speaker);
        }
      });
      
      // Create speakers array with alternating genders for balanced defaults
      // Real LLM outputs should provide proper speaker gender information
      const speakerArray = Array.from(speakerNames).map((name, index) => ({
        name,
        gender: index % 2 === 0 ? 'female' : 'male'
      }));
      updateRequest.speakers = speakerArray as Array<{ name: string; gender: 'male' | 'female' }>;
    }
    
    // Apply metadata fields (tags, theme, story fields)
    if (outputs['tags'] || outputs['metadata']) {
      const metadata = { ...(clip.metadata || {}) };
      
      // Apply tags if provided
      if (outputs['tags'] && Array.isArray(outputs['tags'])) {
        (metadata as any)['tags'] = outputs['tags'];
      }
      
      // Merge metadata from outputs
      if (outputs['metadata'] && typeof outputs['metadata'] === 'object') {
        Object.assign(metadata, outputs['metadata']);
      }
      
      updateRequest.metadata = metadata;
    }
    
    // Apply word count as top-level field
    if (outputs['wordCount'] && typeof outputs['wordCount'] === 'number') {
      updateRequest.wordCount = outputs['wordCount'];
    }
    
    // Update clip via API
    const updatedClip = await adminClipsService.updateClip(clip.id, updateRequest);
    
    // Update current clip state
    currentClip = updatedClip;
    
    // Re-render script tab
    const contentArea = document.getElementById('editor-content-area');
    if (contentArea) {
      await renderScriptTab(contentArea, updatedClip);
    }
    
    toast.success('Regeneration applied successfully');
    
  } catch (error: any) {
    logger.error('admin: Failed to apply regeneration', { error });
    toast.error(`Failed to apply regeneration: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Render script tab
 */
async function renderScriptTab(container: HTMLElement, clip: AdminClip): Promise<void> {
  logger.info('admin: Rendering script tab', { format: clip.format });
  
  const tabContent = document.createElement('div');
  tabContent.className = 'editor-tab-content';
  
  // Add regeneration results section at the top
  await renderRegenerationResultsSection(tabContent, clip);
  
  // Check format and render appropriate editor
  const format = clip.format || 'monologue';
  
  if (format === 'dialogue') {
    renderDialogueEditor(tabContent, clip);
  } else if (format === 'monologue') {
    renderMonologueEditor(tabContent, clip);
  } else {
    // Mixed or unknown format - use generic textarea
    renderGenericScriptEditor(tabContent, clip);
  }
  
  // Info box at bottom
  const infoBox = document.createElement('div');
  infoBox.className = 'editor-info-box';
  const infoStrong = document.createElement('strong');
  infoStrong.textContent = 'Note:';
  infoBox.appendChild(infoStrong);
  infoBox.appendChild(document.createTextNode(' Changes to the script may require audio regeneration and re-validation.'));
  tabContent.appendChild(infoBox);
  
  container.innerHTML = '';
  container.appendChild(tabContent);
}

/**
 * Render dialogue editor (speakers + turns)
 */
function renderDialogueEditor(container: HTMLElement, clip: AdminClip): void {
  // Parse existing script data
  let speakers: Array<{ name: string; gender: 'male' | 'female' }> = [];
  let turns: Array<{ speaker: string; text: string }> = [];
  
  if (Array.isArray(clip.script)) {
    turns = clip.script;
    // Extract unique speakers from script if speakers array doesn't exist
    if (!clip.speakers || clip.speakers.length === 0) {
      const speakerNames = new Set(turns.map(t => t.speaker));
      speakers = Array.from(speakerNames).map(name => ({ name, gender: 'female' as const }));
    } else {
      speakers = [...clip.speakers];
    }
  } else if (clip.speakers) {
    speakers = [...clip.speakers];
  }
  
  // Start with at least one empty speaker form
  if (speakers.length === 0) {
    speakers.push({ name: '', gender: 'female' });
  }
  
  // Re-render function
  const rerender = () => {
    // Update clip data BEFORE re-rendering - keep all speakers during editing
    clip.speakers = speakers;
    clip.script = turns;
    
    renderScriptTab(container.parentElement!, clip).catch(err => {
      logger.error('admin: Failed to re-render script tab', { error: err });
    });
    markAsChanged();
  };
  
  // Speakers Section
  const speakersSection = document.createElement('div');
  speakersSection.className = 'form-group';
  speakersSection.style.marginBottom = '1.5rem';
  
  const speakersLabel = document.createElement('label');
  speakersLabel.textContent = 'Speakers';
  speakersLabel.style.display = 'block';
  speakersLabel.style.marginBottom = '0.75rem';
  speakersLabel.style.fontSize = '1.1rem';
  speakersLabel.style.fontWeight = '600';
  speakersSection.appendChild(speakersLabel);
  
  // Speakers list
  const speakersList = document.createElement('div');
  speakersList.className = 'speakers-list';
  speakersList.style.marginBottom = '0.75rem';
  
  speakers.forEach((speaker, index) => {
    const speakerRow = document.createElement('div');
    speakerRow.className = 'speaker-row';
    speakerRow.style.display = 'flex';
    speakerRow.style.gap = '0.75rem';
    speakerRow.style.alignItems = 'center';
    speakerRow.style.padding = '0.75rem';
    speakerRow.style.border = '1px solid #ddd';
    speakerRow.style.borderRadius = '4px';
    speakerRow.style.marginBottom = '0.75rem';
    speakerRow.style.backgroundColor = '#f9f9f9';
    
    // Name input
    const nameWrapper = document.createElement('div');
    nameWrapper.style.flex = '0 1 auto';
    nameWrapper.style.minWidth = '120px';
    nameWrapper.style.maxWidth = '200px';
    
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'form-input';
    nameInput.value = speaker.name;
    nameInput.placeholder = 'Name';
    nameInput.maxLength = 50;
    nameInput.style.width = '100%';
    
    // Validation state
    const validateName = () => {
      const value = nameInput.value.trim();
      if (value.length === 0) {
        nameInput.style.borderColor = '#dc2626';
        return false;
      } else {
        nameInput.style.borderColor = '#10b981';
        return true;
      }
    };
    
    nameInput.addEventListener('input', () => {
      const oldName = speaker.name;
      speaker.name = nameInput.value;
      validateName();
      // Update turns that use this speaker (by old name)
      updateTurnDropdowns(oldName, speaker.name);
      markAsChanged();
    });
    
    nameInput.addEventListener('blur', () => {
      validateName();
    });
    
    nameWrapper.appendChild(nameInput);
    speakerRow.appendChild(nameWrapper);
    
    // Gender radio buttons
    const genderWrapper = document.createElement('fieldset');
    genderWrapper.style.border = 'none';
    genderWrapper.style.padding = '0';
    genderWrapper.style.margin = '0';
    genderWrapper.style.minWidth = '180px';
    genderWrapper.style.flex = '1';
    
    const genderContainer = document.createElement('div');
    genderContainer.style.display = 'flex';
    genderContainer.style.gap = '1rem';
    genderContainer.style.alignItems = 'center';
    
    // Male radio
    const maleLabel = document.createElement('label');
    maleLabel.style.display = 'flex';
    maleLabel.style.alignItems = 'center';
    maleLabel.style.gap = '0.25rem';
    maleLabel.style.cursor = 'pointer';
    
    const maleRadio = document.createElement('input');
    maleRadio.type = 'radio';
    maleRadio.name = `speaker-gender-${index}`;
    maleRadio.value = 'male';
    maleRadio.checked = speaker.gender === 'male';
    maleRadio.addEventListener('change', () => {
      if (maleRadio.checked) {
        speaker.gender = 'male';
        markAsChanged();
      }
    });
    maleLabel.appendChild(maleRadio);
    maleLabel.appendChild(document.createTextNode('Male'));
    genderContainer.appendChild(maleLabel);
    
    // Female radio
    const femaleLabel = document.createElement('label');
    femaleLabel.style.display = 'flex';
    femaleLabel.style.alignItems = 'center';
    femaleLabel.style.gap = '0.25rem';
    femaleLabel.style.cursor = 'pointer';
    
    const femaleRadio = document.createElement('input');
    femaleRadio.type = 'radio';
    femaleRadio.name = `speaker-gender-${index}`;
    femaleRadio.value = 'female';
    femaleRadio.checked = speaker.gender === 'female';
    femaleRadio.addEventListener('change', () => {
      if (femaleRadio.checked) {
        speaker.gender = 'female';
        markAsChanged();
      }
    });
    femaleLabel.appendChild(femaleRadio);
    femaleLabel.appendChild(document.createTextNode('Female'));
    genderContainer.appendChild(femaleLabel);
    
    genderWrapper.appendChild(genderContainer);
    speakerRow.appendChild(genderWrapper);
    
    // Remove button (right-aligned)
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'btn btn--sm';
    removeBtn.textContent = '×';
    removeBtn.title = 'Remove speaker';
    removeBtn.style.width = '32px';
    removeBtn.style.minWidth = '32px';
    removeBtn.style.maxWidth = '32px';
    removeBtn.style.height = '32px';
    removeBtn.style.padding = '0';
    removeBtn.style.fontSize = '20px';
    removeBtn.style.lineHeight = '1';
    removeBtn.style.border = '1px solid #ddd';
    removeBtn.style.backgroundColor = 'white';
    removeBtn.style.color = '#666';
    removeBtn.style.cursor = 'pointer';
    removeBtn.style.borderRadius = '4px';
    removeBtn.style.flexShrink = '0';
    removeBtn.style.marginLeft = 'auto';
    removeBtn.disabled = speakers.length === 1;
    removeBtn.addEventListener('mouseover', () => {
      if (!removeBtn.disabled) {
        removeBtn.style.backgroundColor = '#fee';
        removeBtn.style.borderColor = '#dc2626';
        removeBtn.style.color = '#dc2626';
      }
    });
    removeBtn.addEventListener('mouseout', () => {
      if (!removeBtn.disabled) {
        removeBtn.style.backgroundColor = 'white';
        removeBtn.style.borderColor = '#ddd';
        removeBtn.style.color = '#666';
      }
    });
    removeBtn.addEventListener('click', () => {
      const speakerName = speaker.name || 'this speaker';
      if (confirm(`Remove speaker "${speakerName}"? This will remove their turns from the dialogue.`)) {
        // Remove speaker
        speakers.splice(index, 1);
        // Remove turns using this speaker (more efficient filter approach)
        turns = turns.filter(t => t.speaker !== speaker.name);
        rerender();
      }
    });
    speakerRow.appendChild(removeBtn);
    
    speakersList.appendChild(speakerRow);
  });
  
  speakersSection.appendChild(speakersList);
  
  // Add Speaker button
  const addSpeakerBtn = document.createElement('button');
  addSpeakerBtn.type = 'button';
  addSpeakerBtn.className = 'btn btn--secondary btn--sm';
  addSpeakerBtn.textContent = '+ Add Speaker';
  // Disable if already at max 2 speakers
  addSpeakerBtn.disabled = speakers.length >= 2;
  addSpeakerBtn.addEventListener('click', () => {
    if (speakers.length < 2) {
      speakers.push({ name: '', gender: 'female' });
      rerender();
    }
  });
  speakersSection.appendChild(addSpeakerBtn);
  
  container.appendChild(speakersSection);
  
  // Dialogue Turns Section
  const turnsSection = document.createElement('div');
  turnsSection.className = 'form-group';
  turnsSection.style.marginTop = '1.5rem';
  
  const turnsLabel = document.createElement('label');
  turnsLabel.textContent = 'Dialogue Turns';
  turnsLabel.style.display = 'block';
  turnsLabel.style.marginBottom = '0.75rem';
  turnsLabel.style.fontSize = '1.1rem';
  turnsLabel.style.fontWeight = '600';
  turnsSection.appendChild(turnsLabel);
  
  // Helper function to get valid speakers (non-empty names)
  const getValidSpeakers = () => speakers.filter(s => s.name.trim().length > 0);
  
  // Helper function to update turn dropdowns
  const updateTurnDropdowns = (oldName?: string, newName?: string) => {
    const allDropdowns = container.querySelectorAll('.turn-speaker-dropdown') as NodeListOf<HTMLSelectElement>;
    const validSpeakers = getValidSpeakers();
    
    allDropdowns.forEach((dropdown) => {
      const currentValue = dropdown.value;
      dropdown.innerHTML = '';
      
      validSpeakers.forEach(s => {
        const option = document.createElement('option');
        option.value = s.name;
        option.textContent = s.name;
        option.selected = s.name === currentValue;
        dropdown.appendChild(option);
      });
      
      // If oldName and newName are provided, update the dropdown if it was showing oldName
      // Handle empty string oldName explicitly for edge cases
      if (newName && currentValue === oldName) {
        dropdown.value = newName;
        const turnIndex = parseInt(dropdown.dataset['turnIndex'] || '0');
        if (turns[turnIndex]) {
          turns[turnIndex].speaker = newName;
        }
      }
      // If current value no longer exists, select first speaker
      else if (!validSpeakers.find(s => s.name === currentValue) && validSpeakers.length > 0) {
        const firstSpeaker = validSpeakers[0];
        if (firstSpeaker) {
          dropdown.value = firstSpeaker.name;
          const turnIndex = parseInt(dropdown.dataset['turnIndex'] || '0');
          if (turns[turnIndex]) {
            turns[turnIndex].speaker = firstSpeaker.name;
          }
        }
      }
    });
  };
  
  // Turns list
  const turnsList = document.createElement('div');
  turnsList.className = 'turns-list';
  turnsList.style.marginBottom = '0.75rem';
  
  const validSpeakers = getValidSpeakers();
  
  if (turns.length === 0) {
    const emptyMsg = document.createElement('p');
    emptyMsg.style.color = '#666';
    emptyMsg.style.fontStyle = 'italic';
    if (validSpeakers.length === 0) {
      emptyMsg.textContent = 'No dialogue turns yet. Add speakers above, then create turns.';
    } else {
      emptyMsg.textContent = 'No dialogue turns yet. Click "+ Add Turn" to start building the conversation.';
    }
    turnsList.appendChild(emptyMsg);
  } else {
    turns.forEach((turn, index) => {
      const turnRow = document.createElement('div');
      turnRow.className = 'turn-row';
      turnRow.style.display = 'flex';
      turnRow.style.gap = '0.75rem';
      turnRow.style.alignItems = 'center';
      turnRow.style.padding = '0.75rem';
      turnRow.style.border = '1px solid #ddd';
      turnRow.style.borderRadius = '4px';
      turnRow.style.marginBottom = '0.75rem';
      turnRow.style.backgroundColor = '#f9f9f9';
      
      // Turn number
      const turnNumber = document.createElement('div');
      turnNumber.textContent = `${index + 1}.`;
      turnNumber.style.fontWeight = '600';
      turnNumber.style.minWidth = '30px';
      turnRow.appendChild(turnNumber);
      
      // Speaker dropdown
      const speakerWrapper = document.createElement('div');
      speakerWrapper.style.flex = '0 1 auto';
      speakerWrapper.style.minWidth = '100px';
      speakerWrapper.style.maxWidth = '200px';
      
      const speakerDropdown = document.createElement('select');
      speakerDropdown.className = 'form-input turn-speaker-dropdown';
      speakerDropdown.dataset['turnIndex'] = String(index);
      speakerDropdown.style.width = '100%';
      speakerDropdown.title = 'Speaker';
      
      validSpeakers.forEach(s => {
        const option = document.createElement('option');
        option.value = s.name;
        option.textContent = s.name;
        option.selected = s.name === turn.speaker;
        speakerDropdown.appendChild(option);
      });
      
      speakerDropdown.addEventListener('change', () => {
        turn.speaker = speakerDropdown.value;
        markAsChanged();
      });
      
      speakerWrapper.appendChild(speakerDropdown);
      turnRow.appendChild(speakerWrapper);
      
      // Text textarea
      const textWrapper = document.createElement('div');
      textWrapper.style.flex = '1';
      
      const textArea = document.createElement('textarea');
      textArea.className = 'form-input';
      textArea.value = turn.text;
      textArea.placeholder = 'Text';
      textArea.rows = 1;
      textArea.style.width = '100%';
      textArea.style.resize = 'vertical';
      textArea.style.overflow = 'hidden';
      
      // Set initial height based on existing content
      // Use setTimeout to ensure DOM is ready for scrollHeight calculation
      setTimeout(() => {
        textArea.style.height = 'auto';
        textArea.style.height = Math.min(textArea.scrollHeight, 250) + 'px';
      }, 0);
      
      // Auto-expand textarea
      textArea.addEventListener('input', () => {
        turn.text = textArea.value;
        // Reset height to calculate new scrollHeight
        textArea.style.height = 'auto';
        // Set height based on content, max 250px
        textArea.style.height = Math.min(textArea.scrollHeight, 250) + 'px';
        markAsChanged();
      });
      
      textArea.addEventListener('blur', () => {
        if (textArea.value.trim().length === 0) {
          textArea.style.borderColor = '#dc2626';
        } else {
          textArea.style.borderColor = '';
        }
      });
      
      textWrapper.appendChild(textArea);
      turnRow.appendChild(textWrapper);
      
      // Delete button
      const deleteBtn = document.createElement('button');
      deleteBtn.type = 'button';
      deleteBtn.className = 'btn btn--sm';
      deleteBtn.textContent = '×';
      deleteBtn.title = 'Delete turn';
      deleteBtn.style.width = '32px';
      deleteBtn.style.minWidth = '32px';
      deleteBtn.style.maxWidth = '32px';
      deleteBtn.style.height = '32px';
      deleteBtn.style.padding = '0';
      deleteBtn.style.fontSize = '20px';
      deleteBtn.style.lineHeight = '1';
      deleteBtn.style.border = '1px solid #ddd';
      deleteBtn.style.backgroundColor = 'white';
      deleteBtn.style.color = '#666';
      deleteBtn.style.cursor = 'pointer';
      deleteBtn.style.borderRadius = '4px';
      deleteBtn.style.alignSelf = 'center';
      deleteBtn.style.flexShrink = '0';
      deleteBtn.addEventListener('mouseover', () => {
        deleteBtn.style.backgroundColor = '#fee';
        deleteBtn.style.borderColor = '#dc2626';
        deleteBtn.style.color = '#dc2626';
      });
      deleteBtn.addEventListener('mouseout', () => {
        deleteBtn.style.backgroundColor = 'white';
        deleteBtn.style.borderColor = '#ddd';
        deleteBtn.style.color = '#666';
      });
      deleteBtn.addEventListener('click', () => {
        if (confirm('Delete this turn?')) {
          turns.splice(index, 1);
          rerender();
        }
      });
      turnRow.appendChild(deleteBtn);
      
      turnsList.appendChild(turnRow);
    });
  }
  
  turnsSection.appendChild(turnsList);
  
  // Add Turn button
  const addTurnBtn = document.createElement('button');
  addTurnBtn.type = 'button';
  addTurnBtn.className = 'btn btn--secondary btn--sm';
  addTurnBtn.textContent = '+ Add Turn';
  addTurnBtn.disabled = validSpeakers.length === 0;
  
  if (validSpeakers.length === 0) {
    const helperText = document.createElement('p');
    helperText.style.color = '#666';
    helperText.style.fontSize = '0.875rem';
    helperText.style.fontStyle = 'italic';
    helperText.style.marginTop = '0.5rem';
    helperText.textContent = 'Add speakers above before creating dialogue turns';
    turnsSection.appendChild(helperText);
  }
  
  addTurnBtn.addEventListener('click', () => {
    if (validSpeakers.length > 0) {
      // Determine which speaker to use for the new turn
      let nextSpeaker = validSpeakers[0];
      
      // If there are existing turns and 2 speakers, alternate
      if (turns.length > 0 && validSpeakers.length === 2) {
        const lastTurn = turns[turns.length - 1];
        // Find the other speaker (not the last one used)
        if (lastTurn) {
          const otherSpeaker = validSpeakers.find(s => s.name !== lastTurn.speaker);
          if (otherSpeaker) {
            nextSpeaker = otherSpeaker;
          }
        }
      }
      
      if (nextSpeaker) {
        turns.push({ 
          speaker: nextSpeaker.name, 
          text: '' 
        });
        rerender();
        // Focus on the new textarea after render
        // Query from root since rerender() replaces container's content
        setTimeout(() => {
          const root = container.parentElement ?? container;
          const textareas = root.querySelectorAll('textarea');
          const lastTextarea = textareas[textareas.length - 1] as HTMLTextAreaElement | undefined;
          if (lastTextarea) {
            lastTextarea.focus();
          }
        }, 100);
      }
    }
  });
  turnsSection.appendChild(addTurnBtn);
  
  container.appendChild(turnsSection);
  
  // Update clip data - keep ALL speakers during editing (including empty ones)
  // They'll be filtered when the form is saved
  clip.speakers = speakers;
  clip.script = turns;
}

/**
 * Render monologue editor (simple textarea)
 */
function renderMonologueEditor(container: HTMLElement, clip: AdminClip): void {
  const scriptText = typeof clip.script === 'string' 
    ? clip.script 
    : '';
  
  const formGroup = document.createElement('div');
  formGroup.className = 'form-group';
  
  const label = document.createElement('label');
  label.setAttribute('for', 'clip-script');
  label.textContent = 'Script';
  formGroup.appendChild(label);
  
  const textarea = document.createElement('textarea');
  textarea.id = 'clip-script';
  textarea.className = 'form-textarea';
  textarea.rows = 20;
  textarea.value = scriptText;
  textarea.placeholder = 'Enter the monologue script text here...';
  textarea.addEventListener('input', () => {
    clip.script = textarea.value;
    markAsChanged();
  });
  formGroup.appendChild(textarea);
  
  container.appendChild(formGroup);
}

/**
 * Render generic script editor (for mixed or unknown formats)
 */
function renderGenericScriptEditor(container: HTMLElement, clip: AdminClip): void {
  const scriptText = typeof clip.script === 'string' 
    ? clip.script 
    : JSON.stringify(clip.script, null, 2);
  
  const formGroup = document.createElement('div');
  formGroup.className = 'form-group';
  
  const label = document.createElement('label');
  label.setAttribute('for', 'clip-script');
  label.textContent = 'Script (JSON)';
  formGroup.appendChild(label);
  
  const textarea = document.createElement('textarea');
  textarea.id = 'clip-script';
  textarea.className = 'form-textarea';
  textarea.rows = 20;
  textarea.value = scriptText;
  textarea.addEventListener('input', () => markAsChanged());
  formGroup.appendChild(textarea);
  
  container.appendChild(formGroup);
}

/**
 * Render questions tab
 */
function renderQuestionsTab(container: HTMLElement, clip: AdminClip): void {
  const questionsJson = JSON.stringify(clip.questions || [], null, 2);
  
  container.innerHTML = `
    <div class="editor-tab-content">
      <div class="form-group">
        <label for="clip-questions">Questions (JSON)</label>
        <textarea id="clip-questions" class="form-textarea" rows="20">${questionsJson}</textarea>
      </div>
      
      <div class="editor-info-box">
        <strong>Question Count:</strong> ${(clip.questions || []).length}
      </div>
    </div>
  `;
  
  const questionsInput = container.querySelector('#clip-questions') as HTMLTextAreaElement;
  if (questionsInput) {
    questionsInput.addEventListener('input', () => markAsChanged());
  }
}

/**
 * Render audio tab
 */
function renderAudioTab(container: HTMLElement, clip: AdminClip): void {
  container.innerHTML = `
    <div class="editor-tab-content">
      <div class="form-group">
        <label>Audio Status</label>
        <div class="audio-status">
          ${clip.audioUrl ? '✓ Audio available' : '✗ No audio'}
        </div>
      </div>
      
      ${clip.audioUrl ? `
        <div class="form-group">
          <label>Audio URL</label>
          <input type="text" class="form-input" value="${clip.audioUrl}" readonly />
        </div>
        
        <div class="form-group">
          <label>Audio Player</label>
          <audio controls src="${clip.audioUrl}" style="width: 100%;"></audio>
        </div>
      ` : ''}
      
      <div class="form-group">
        <label>Voice ID</label>
        <input type="text" id="clip-voice-id" class="form-input" value="${clip.voiceId || ''}" placeholder="Not set" />
      </div>
      
      ${can(Permission.TRIGGER_AUDIO_GENERATION) ? `
        <button class="btn btn--primary" id="generate-audio-btn">
          Generate Audio
        </button>
      ` : ''}
    </div>
  `;
  
  const generateBtn = container.querySelector('#generate-audio-btn');
  if (generateBtn) {
    generateBtn.addEventListener('click', () => handleGenerateAudio(clip.id));
  }
}

/**
 * Render history tab
 */
function renderHistoryTab(container: HTMLElement, clip: AdminClip): void {
  container.innerHTML = `
    <div class="editor-tab-content">
      <h3>Validation History</h3>
      <div id="validation-history-container">
        Loading...
      </div>
    </div>
  `;
  
  // Load validation history
  adminClipsService.getValidationHistory(clip.id)
    .then(history => {
      const historyContainer = container.querySelector('#validation-history-container');
      if (!historyContainer) return;
      
      if (history.length === 0) {
        historyContainer.innerHTML = '<p>No validation history available.</p>';
        return;
      }
      
      historyContainer.innerHTML = '';
      history.forEach((entry: any) => {
        const entryDiv = document.createElement('div');
        entryDiv.className = 'history-entry';
        entryDiv.innerHTML = `
          <strong>${new Date(entry.timestamp).toLocaleString()}</strong>
          <span class="validation-status-badge validation-status-badge--${entry.report.overallStatus}">
            ${entry.report.overallStatus}
          </span>
        `;
        historyContainer.appendChild(entryDiv);
      });
    });
}

/**
 * Create editor actions footer
 */
function createEditorActions(clip: AdminClip): HTMLElement {
  const actions = document.createElement('div');
  actions.className = 'editor-actions';
  
  const saveBtn = document.createElement('button');
  saveBtn.className = 'btn btn--primary';
  saveBtn.textContent = 'Save Changes';
  saveBtn.id = 'save-clip-btn';
  saveBtn.disabled = !hasUnsavedChanges;
  saveBtn.addEventListener('click', () => handleSaveClip(clip.id));
  actions.appendChild(saveBtn);
  
  // State-specific actions
  if (clip.state === ClipState.Draft && can(Permission.SUBMIT_FOR_REVIEW)) {
    const submitBtn = document.createElement('button');
    submitBtn.className = 'btn btn--success';
    submitBtn.textContent = 'Submit for Review';
    submitBtn.addEventListener('click', () => handleSubmitForReview(clip.id));
    actions.appendChild(submitBtn);
  }
  
  if (clip.state === ClipState.TextReview && can(Permission.APPROVE_TEXT)) {
    const approveBtn = document.createElement('button');
    approveBtn.className = 'btn btn--success';
    approveBtn.textContent = 'Approve Text';
    approveBtn.addEventListener('click', () => handleApproveText(clip.id));
    actions.appendChild(approveBtn);
  }
  
  return actions;
}

/**
 * Mark editor as having unsaved changes
 */
function markAsChanged(): void {
  hasUnsavedChanges = true;
  const saveBtn = document.getElementById('save-clip-btn') as HTMLButtonElement;
  if (saveBtn) {
    saveBtn.disabled = false;
  }
  
  // Update validation status to show "needs validation"
  const validationSection = document.getElementById('validation-section');
  if (validationSection && currentClip) {
    const statusIndicator = createValidationStatusIndicator(
      currentClip.validationReport || null,
      true
    );
    const existing = validationSection.querySelector('.validation-status-indicator');
    if (existing) {
      existing.replaceWith(statusIndicator);
    }
  }
}

/**
 * Handle validate clip action
 */
async function handleValidateClip(clipId: string): Promise<void> {
  logger.info('admin: Validating clip', { clipId });
  
  const section = document.getElementById('validation-section');
  if (!section) return;
  
  try {
    // Show loading state
    const loadingToast = toast.showToast('Running validation...', 'info', 30000);
    
    // Run validation
    const result = await adminClipsService.validateClip(clipId);
    
    // Dismiss loading toast before showing result
    toast.dismissToast(loadingToast);
    
    // Update current clip
    if (currentClip) {
      currentClip.validationReport = result.report;
    }
    
    // Re-render validation section
    const newSection = createValidationSection(currentClip!);
    section.replaceWith(newSection);
    
    // Show result toast
    if (result.report.overallStatus === 'passed') {
      toast.success('Validation passed!');
    } else if (result.report.overallStatus === 'warning') {
      toast.warning('Validation passed with warnings');
    } else {
      toast.error('Validation failed');
    }
    
  } catch (error) {
    logger.error('admin: Validation failed', { clipId, error });
    toast.error('Validation failed');
  }
}

/**
 * Handle save clip action
 */
async function handleSaveClip(clipId: string): Promise<void> {
  logger.info('admin: Saving clip', { clipId });
  
  if (!currentClip) {
    toast.error('No clip loaded');
    return;
  }
  
  try {
    // Collect form data from metadata tab
    const title = (document.getElementById('clip-title') as HTMLInputElement)?.value;
    const language = (document.getElementById('clip-language') as HTMLSelectElement)?.value;
    const level = (document.getElementById('clip-level') as HTMLSelectElement)?.value;
    const format = (document.getElementById('clip-format') as HTMLSelectElement)?.value;
    
    const updates: UpdateClipRequest = {};
    if (title) updates.title = title;
    if (language) updates.language = language as any;
    if (level) updates.level = level as any;
    if (format) updates.format = format as any;
    
    // Script data comes from currentClip (modified by tab editors)
    if (currentClip.script) {
      // For dialogue format, filter out turns with empty text before saving
      if (currentClip.format === 'dialogue' && Array.isArray(currentClip.script)) {
        updates.script = currentClip.script.filter((turn: any) => 
          turn.text && turn.text.trim().length > 0
        );
      } else {
        updates.script = currentClip.script;
      }
    }
    
    // Speakers data (for dialogue) - filter out empty speakers before saving
    if (currentClip.speakers) {
      const validSpeakers = currentClip.speakers.filter(s => s.name.trim().length > 0);
      if (validSpeakers.length > 0) {
        updates.speakers = validSpeakers;
      }
    }
    
    // Questions data (if modified)
    const questionsJson = (document.getElementById('clip-questions') as HTMLTextAreaElement)?.value;
    if (questionsJson) {
      try {
        updates.questions = JSON.parse(questionsJson);
      } catch (e) {
        toast.error('Invalid questions JSON');
        return;
      }
    }
    
    logger.info('admin: Saving clip updates', { clipId, updates });
    
    // Save updates
    await adminClipsService.updateClip(clipId, updates);
    
    hasUnsavedChanges = false;
    toast.success('Clip saved successfully');
    
    // Disable save button after successful save
    const saveBtn = document.getElementById('save-clip-btn') as HTMLButtonElement;
    if (saveBtn) {
      saveBtn.disabled = true;
    }
    
    if (callbacks) callbacks.onSave(clipId);
    
  } catch (error) {
    logger.error('admin: Failed to save clip', { clipId, error });
    toast.error('Failed to save clip');
  }
}

/**
 * Handle submit for review action
 */
async function handleSubmitForReview(clipId: string): Promise<void> {
  if (!confirm('Submit this clip for review? Validation will be run automatically.')) {
    return;
  }
  
  try {
    // First, run validation
    toast.info('Running validation before submit...');
    const validationResult = await adminClipsService.validateClip(clipId);
    
    // Check for critical failures
    const criticalFailures = validationResult.report.checks.filter(
      c => c.severity === 'critical' && c.status === 'failed'
    );
    
    if (criticalFailures.length > 0) {
      toast.error(`Cannot submit: ${criticalFailures.length} critical validation failures`);
      return;
    }
    
    // Submit for review
    await adminClipsService.submitForReview(clipId);
    toast.success('Clip submitted for review');
    
    if (callbacks) callbacks.onClose();
    
  } catch (error) {
    logger.error('admin: Failed to submit for review', { clipId, error });
    toast.error('Failed to submit clip for review');
  }
}

/**
 * Handle approve text action
 */
async function handleApproveText(clipId: string): Promise<void> {
  if (!confirm('Approve the text for this clip?')) {
    return;
  }
  
  try {
    await adminClipsService.approveText(clipId);
    toast.success('Text approved');
    
    if (callbacks) callbacks.onClose();
    
  } catch (error) {
    logger.error('admin: Failed to approve text', { clipId, error });
    toast.error('Failed to approve text');
  }
}

/**
 * Handle generate audio action
 */
async function handleGenerateAudio(clipId: string): Promise<void> {
  if (!currentClip) {
    toast.error('No clip loaded');
    return;
  }
  
  try {
    // Show audio generation dialog with voice selection
    showAudioGenerationDialog(currentClip, {
      onJobQueued: async (jobId: string) => {
        logger.info('admin: Audio generation job queued', { clipId, jobId });
        toast.success('Audio generation queued');
        
        // Reload clip to reflect updated state (now AudioQueued)
        try {
          const updatedClip = await adminClipsService.getClip(clipId);
          currentClip = updatedClip;
          
          // Refresh the editor UI to show new state
          const container = document.getElementById('editor-content-area')?.parentElement;
          if (container) {
            await renderClipEditor(container, clipId);
          }
        } catch (error) {
          logger.error('admin: Failed to reload clip after audio generation', { error });
          toast.warning('Audio generation started successfully. Please reload the page to see the updated clip status.');
        }
      },
      onCancel: () => {
        logger.info('admin: Audio generation cancelled', { clipId });
      }
    });
  } catch (error) {
    logger.error('admin: Failed to open audio generation dialog', { clipId, error });
    toast.error('Unable to open the audio generation dialog. Please try again.');
  }
}

/**
 * Handle regenerate script action
 */
function handleRegenerateScript(clip: AdminClip): void {
  logger.info('admin: Opening regeneration dialog', { clipId: clip.id });
  
  showRegenerationDialog(
    clip.id,
    clip.language,
    clip.level,
    {
      onJobQueued: (jobId: string) => {
        logger.info('admin: Regeneration job queued', { clipId: clip.id, jobId });
        
        // Show job status dialog
        showJobStatusDialog(jobId);
        
        // TODO: Poll for completion and show diff viewer when done
      },
      onCancel: () => {
        logger.info('admin: Regeneration cancelled');
      }
    }
  );
}

/**
 * Render access denied message
 */
function renderAccessDenied(container: HTMLElement): void {
  container.innerHTML = `
    <div class="access-denied-container">
      <h2 class="access-denied-heading">Access Denied</h2>
      <p class="access-denied-message">You do not have permission to access the clip editor.</p>
    </div>
  `;
}
